import { Button } from "@/components/ui/button";
import { getPopupImage } from "@/lib/popupImages";
import { getAccommodation } from "@/lib/accommodationAmenities";
import { safeColor, useCopyOverride } from "@/lib/copyOverrides";
import { PopupImageCarousel } from "@/components/marketing/PopupImageCarousel";

interface OvernightPopupCardProps {
  onCta: () => void;
}

export function OvernightPopupCard({ onCta }: OvernightPopupCardProps) {
  const headline = useCopyOverride("popup.headline");
  const price = useCopyOverride("popup.price");
  const subline = useCopyOverride("popup.subline");
  const cta = useCopyOverride("popup.cta");
  const imageId = useCopyOverride("popup.image");
  const imagesCsv = useCopyOverride("popup.images");
  const accommodationsCsv = useCopyOverride("popup.accommodations");
  const accent = safeColor(useCopyOverride("popup.accent"));
  const buttonFg = safeColor(useCopyOverride("popup.buttonFg"));

  const image = getPopupImage(imageId);
  const remoteImages = imagesCsv
    .split(",")
    .map((s) => s.trim())
    .filter((s) => /^https:\/\//i.test(s));

  const options = accommodationsCsv
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((id) => getAccommodation(id))
    .filter((o): o is NonNullable<typeof o> => Boolean(o));

  return (
    <div className="flex flex-col">
      {remoteImages.length > 0 ? (
        <PopupImageCarousel
          images={remoteImages}
          alt={headline || "Promotion"}
          className="w-full h-44 sm:h-52"
        />
      ) : (
        <img
          src={image.src}
          alt={image.label}
          className="w-full h-44 sm:h-52 object-cover rounded-xl"
          loading="eager"
        />
      )}

      <div className="pt-4 space-y-3 text-center">
        <h2 className="font-heading text-2xl font-bold text-charcoal">{headline}</h2>
        {price && (
          <p className="text-lg font-semibold text-coral-orange" style={{ color: accent }}>
            {price}
          </p>
        )}
        {subline && <p className="text-sm text-charcoal/70">{subline}</p>}

        {options.length > 0 && (
          <div className="space-y-3 pt-1">
            {options.map((option) => {
              const Icon = option.icon;
              return (
                <div key={option.id} className="text-left">
                  <div className="flex items-center gap-2 text-sm font-semibold text-charcoal">
                    <Icon className="w-4 h-4 text-teal" />
                    <span>{option.label}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {option.amenities.map((amenity) => (
                      <span
                        key={amenity}
                        className="rounded-full bg-muted px-3 py-1 text-xs text-charcoal/80"
                      >
                        {amenity}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <Button
          className="w-full mt-2"
          size="lg"
          onClick={onCta}
          style={{ backgroundColor: accent, color: buttonFg }}
        >
          {cta}
        </Button>
      </div>
    </div>
  );
}
